import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Plus, ArrowRight, Receipt, Download } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// دالة تنسيق المبالغ بالأرقام الإنجليزية وشعار الريال السعودي الجديد
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 ﷼';
  return `${Number(amount).toLocaleString('en-US')} ﷼`;
};

export default function Vouchers() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    voucher_number: '',
    voucher_type: 'قبض',
    amount: '',
    payment_method: 'نقدي',
    case_id: '',
    client_id: '',
    client_name: '',
    description: '',
  });

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vouchers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVouchers(response.data);
    } catch (err) {
      console.error('Error fetching vouchers:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vouchers`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchVouchers();
      setFormData({
        voucher_number: '',
        voucher_type: 'قبض',
        amount: '',
        payment_method: 'نقدي',
        case_id: '',
        client_id: '',
        client_name: '',
        description: '',
      });
    } catch (err) {
      console.error('Error creating voucher:', err);
    }
  };

  const downloadPDF = async (voucherId, voucherNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vouchers/${voucherId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      // إنشاء رابط للتحميل
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `voucher_${voucherNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('حدث خطأ أثناء تحميل الملف');
    }
  };

  const printVoucher = (voucher) => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>سند ${voucher.voucher_type} - ${voucher.voucher_number}</title>
          <style>
            body { font-family: 'Arial', sans-serif; padding: 40px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #C5A059; padding-bottom: 20px; }
            .title { font-size: 24px; font-weight: bold; color: #0F172A; margin-bottom: 10px; }
            .company { font-size: 18px; color: #666; }
            .content { margin: 30px 0; }
            .row { display: flex; justify-content: space-between; margin: 15px 0; padding: 10px; background: #f9f9f7; }
            .label { font-weight: bold; color: #0F172A; }
            .value { color: #333; }
            .amount { font-size: 28px; font-weight: bold; color: #C5A059; text-align: center; margin: 30px 0; padding: 20px; background: #f9f9f7; }
            .footer { margin-top: 50px; display: flex; justify-content: space-between; }
            .signature { text-align: center; }
            .signature-line { border-top: 2px solid #333; width: 200px; margin: 10px auto; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">سند ${voucher.voucher_type}</div>
            <div class="company">مجموعة المحامي هشام يوسف الخياط للمحاماة والاستشارات القانونية</div>
          </div>
          <div class="content">
            <div class="row">
              <div><span class="label">رقم السند:</span> <span class="value">${voucher.voucher_number}</span></div>
              <div><span class="label">التاريخ:</span> <span class="value">${new Date(voucher.created_at).toLocaleDateString('ar-EG')}</span></div>
            </div>
            ${voucher.client_name ? `<div class="row"><div><span class="label">العميل:</span> <span class="value">${voucher.client_name}</span></div></div>` : ''}
            <div class="row">
              <div><span class="label">طريقة الدفع:</span> <span class="value">${voucher.payment_method}</span></div>
            </div>
            <div class="row">
              <div><span class="label">البيان:</span> <span class="value">${voucher.description}</span></div>
            </div>
            <div class="amount">
              المبلغ: ${Number(voucher.amount).toLocaleString('en-US')} ﷼
            </div>
          </div>
          <div class="footer">
            <div class="signature">
              <div>المستلم</div>
              <div class="signature-line"></div>
            </div>
            <div class="signature">
              <div>المحاسب</div>
              <div class="signature-line"></div>
              <div style="margin-top: 10px;">${voucher.created_by_name}</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">السندات</h1>
          </div>
          <button
            data-testid="add-voucher-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            إصدار سند
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4">
          {vouchers.map((voucher) => (
            <div
              key={voucher.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover-lift"
              data-testid={`voucher-card-${voucher.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <Receipt className="w-8 h-8 text-gold mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-heading font-semibold text-primary">
                        سند {voucher.voucher_type} - رقم {voucher.voucher_number}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-body ${
                          voucher.voucher_type === 'قبض'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {voucher.voucher_type}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gold mb-2 font-heading">
                      {formatCurrency(voucher.amount)}
                    </p>
                    {voucher.client_name && (
                      <p className="text-sm text-gray-600 mb-1 font-body">
                        <strong>العميل:</strong> {voucher.client_name}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-1 font-body">
                      <strong>طريقة الدفع:</strong> {voucher.payment_method}
                    </p>
                    <p className="text-sm text-gray-700 mb-2 font-body">
                      <strong>البيان:</strong> {voucher.description}
                    </p>
                    <p className="text-xs text-gray-500 font-body">
                      أصدر بواسطة: {voucher.created_by_name} -{' '}
                      {new Date(voucher.created_at).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => downloadPDF(voucher.id, voucher.voucher_number)}
                    className="px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body flex items-center gap-2"
                    data-testid={`download-voucher-${voucher.id}`}
                  >
                    <Download className="w-4 h-4" />
                    تحميل PDF
                  </button>
                  <button
                    onClick={() => printVoucher(voucher)}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-900 transition-all font-body"
                    data-testid={`print-voucher-${voucher.id}`}
                  >
                    طباعة
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {vouchers.length === 0 && (
          <div className="text-center py-16" data-testid="no-vouchers">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد سندات مسجلة</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="voucher-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">إصدار سند جديد</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    رقم السند
                  </label>
                  <input
                    data-testid="voucher-number"
                    type="text"
                    value={formData.voucher_number}
                    onChange={(e) => setFormData({ ...formData, voucher_number: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    نوع السند
                  </label>
                  <select
                    data-testid="voucher-type"
                    value={formData.voucher_type}
                    onChange={(e) => setFormData({ ...formData, voucher_type: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  >
                    <option value="قبض">قبض</option>
                    <option value="صرف">صرف</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  المبلغ (ريال)
                </label>
                <input
                  data-testid="voucher-amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  طريقة الدفع
                </label>
                <select
                  data-testid="voucher-payment-method"
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="نقدي">نقدي</option>
                  <option value="شيك">شيك</option>
                  <option value="تحويل بنكي">تحويل بنكي</option>
                  <option value="بطاقة ائتمان">بطاقة ائتمان</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  اسم العميل (اختياري)
                </label>
                <input
                  data-testid="voucher-client"
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  البيان
                </label>
                <textarea
                  data-testid="voucher-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="تفاصيل السند..."
                />
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="voucher-submit"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  إصدار
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
